/* =================== VEHICLES (CARS) =================== */
export const VEHICLE_CATEGORIES = [
  { id: "bike",              label: "Bike",      short: "Bike",      priceKey: "priceHatchback" },
  { id: "hatchback_compact", label: "Hatchback", short: "Hatchback", priceKey: "priceHatchback" },
  { id: "sedan",             label: "Sedan",     short: "Sedan",     priceKey: "priceHatchback" },
  { id: "fullsize_suv",      label: "SUV",       short: "SUV",       priceKey: "priceSUV" },
];

/* =================== PACKAGES (Subscription) ===================
   Stored in Firestore `packages` collection (auto-seeded by admin). */
export const PACKAGES = [
  { id: "saver",       code: "A", name: "BayWash Saver",       durationMonths: 3, washes: "4 Regular Washes",            washCount: 4, priceHatchback: 1449, priceSUV: 1749 },
  { id: "saver_pro",   code: "B", name: "BayWash Saver Pro",   durationMonths: 4, washes: "3 Regular + 1 Detailed Wash", washCount: 4, priceHatchback: 2499, priceSUV: 2799 },
  { id: "premium",     code: "C", name: "BayWash Premium",     durationMonths: 3, washes: "4 Premium Washes",            washCount: 4, priceHatchback: 2449, priceSUV: 2749 },
  { id: "premium_pro", code: "D", name: "BayWash Premium Pro", durationMonths: 4, washes: "3 Premium + 1 Detailed Wash", washCount: 4, priceHatchback: 3449, priceSUV: 3749 },
];

/* =================== INDIVIDUAL RATES (Single Wash) ===================
   Stored in Firestore `individual_rates` collection (auto-seeded by admin).
   Bike has its own flat-rate doc (washPolish + optional waxing) instead of
   the basic/premium × interiorExterior/exteriorOnly matrix used by cars. */
export const INDIVIDUAL_RATES = [
  { id: "basic_hatchback",   label: "Basic Hatchback",         interiorExterior: 450, exteriorOnly: 300 },
  { id: "basic_suv",         label: "Basic SUV",               interiorExterior: 550, exteriorOnly: 350 },
  { id: "premium_hatchback", label: "Premium Hatchback/Sedan", interiorExterior: 750, exteriorOnly: 499 },
  { id: "premium_suv",       label: "Premium SUV",             interiorExterior: 850, exteriorOnly: 599 },
  { id: "bike_wash",         label: "Bike Wash & Polish",      washPolish: 149, waxing: 149 },
];

export const WASH_TYPES = [
  { id: "exteriorOnly",      label: "Exterior Only",       short: "EXT" },
  { id: "interiorExterior",  label: "Interior + Exterior", short: "INT+EXT" },
];

/* ---------- SLOT CONFIG ----------
   20-min slots from 9:00 AM to 6:00 PM.
   1:00-2:00 PM is LUNCH (no slots).
   The :40 slot of the FIRST hour of each 2-hour window is REMOVED
   (i.e. 9:40, 11:40, 14:40, 16:40 don't exist).
   Each slot can hold MAX_PER_SLOT (2) concurrent bookings. */
export const MAX_BOOKING_DAYS = 5; // customers can book today + up to 4 days ahead
export const LUNCH_HOUR = 13;
export const SLOT_MINUTES = 20;
export const DAY_START_HOUR = 9;
export const DAY_END_HOUR = 18;
export const MAX_PER_SLOT = 2;
const FIRST_HOUR_OF_WINDOW = [9, 11, 14, 16];

const buildSlots = () => {
  const slots = [];
  for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h++) {
    if (h === LUNCH_HOUR) continue;
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      // Skip the :40 slot of the first hour of each 2-hour window
      if (FIRST_HOUR_OF_WINDOW.includes(h) && m === 40) continue;
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
};
export const TIME_SLOTS = buildSlots();

export const WINDOWS = [
  { key: "09-11", label: "09:00 — 11:00", from: 9,  to: 11 },
  { key: "11-13", label: "11:00 — 01:00 PM", from: 11, to: 13 },
  { key: "14-16", label: "02:00 — 04:00 PM", from: 14, to: 16 },
  { key: "16-18", label: "04:00 — 06:00 PM", from: 16, to: 18 },
];

export const windowForSlot = (slot) => {
  const h = parseInt(slot.split(":")[0], 10);
  return WINDOWS.find((w) => h >= w.from && h < w.to)?.key || null;
};

export const buildSlotAvailability = (bookedSlots, selectedDate = null) => {
  // `bookedSlots` is the FULL list of taken slot strings (can repeat if 2 bookings same slot).
  const counts = {};
  bookedSlots.forEach((s) => {
    counts[s] = (counts[s] || 0) + 1;
  });

  // Detect if the selected date is today — if so, mark past slots as unavailable.
  let cutoffMinutes = -1;
  if (selectedDate) {
    const today = new Date();
    const sel = new Date(selectedDate);
    const sameDay =
      sel.getFullYear() === today.getFullYear() &&
      sel.getMonth() === today.getMonth() &&
      sel.getDate() === today.getDate();
    if (sameDay) cutoffMinutes = today.getHours() * 60 + today.getMinutes() + 30;
  }

  const map = {};
  TIME_SLOTS.forEach((s) => {
    const [hh, mm] = s.split(":").map(Number);
    const slotMinutes = hh * 60 + mm;
    if (cutoffMinutes >= 0 && slotMinutes <= cutoffMinutes) {
      map[s] = { ok: false, reason: "PAST", past: true, count: counts[s] || 0 };
      return;
    }
    const c = counts[s] || 0;
    if (c >= MAX_PER_SLOT) map[s] = { ok: false, reason: "FULL", count: c };
    else map[s] = { ok: true, count: c };
  });
  // Per-window counts for display
  const windowCounts = {};
  TIME_SLOTS.forEach((s) => {
    const w = windowForSlot(s);
    if (w) windowCounts[w] = (windowCounts[w] || 0) + (counts[s] || 0);
  });
  return { map, counts: windowCounts, slotCounts: counts };
};

export const BOOKING_STATUSES = {
  pending:   { label: "Pending",   color: "bg-[#FFC000] text-black" },
  confirmed: { label: "Confirmed", color: "bg-[#002FA7] text-white" },
  completed: { label: "Completed", color: "bg-[#00D05A] text-black" },
  cancelled: { label: "Cancelled", color: "bg-[#FF2A2A] text-white" },
};
const STATUS_FALLBACK = { label: "Unknown", color: "bg-[#D4D4D8] text-[#525252]" };
export const getBookingStatusInfo = (status) => BOOKING_STATUSES[status] || STATUS_FALLBACK;
